import React, { useContext, useEffect, useState } from "react";
import {
  Tag,
  Badge,
  Empty,
  Table,
  Button,
  Modal,
  Typography,
  Row,
  Col,
  Card,
  DatePicker,
} from "antd";
import {
  LoadingOutlined,
  CalendarOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { get_language, translate } from "../../locale";
import moment from "moment";

import DashServices from "../../services/DashServices";
import { useParams } from "react-router-dom";
import UserServices from "../../services/UserServices";
import { AdminContext } from "../../providers/AdminContext";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import SecondaryButton from "../Buttons/SecondaryButton";
import PrimaryButton from "../Buttons/PrimaryButton";

const localizer = momentLocalizer(moment);

const { Title, Text } = Typography;

const PatientMedicineTakenTable = () => {
  const [loading, setLoading] = useState(false);
  const [loadUser, setLoadUser] = useState(false);
  const [dataTable, setDataTable] = useState(null);
  const [data, setData] = useState(null);
  const [patient, setPatient] = useState(null);
  const [dataMedicine, setDataMedicine] = useState(null);
  const [modalDetails, setModalDetails] = useState(false);
  const [showAs, setShowAs] = useState("calendar");
  const weekDays = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
  const { username, subscription_medicine_patient_id } = useParams();
  const { user } = useContext(AdminContext);
  const [date, setDate] = useState(
    moment().startOf("isoWeek").format("YYYY-MM-DD")
  );
  const defaultDateInterval = {
    from: moment().startOf("isoWeek").format("YYYY-MM-DD"),
    to: moment().startOf("isoWeek").add(6, "days").format("YYYY-MM-DD"),
  };
  const [dateInterval, setDateInterval] = useState(defaultDateInterval);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    loadPatientInfo();
  }, []);

  useEffect(() => {
    getPatientMedicines();
  }, [date]);

  useEffect(() => {
    if (showAs === "calendar") {
      if (isSearching) {
        setDateInterval(defaultDateInterval);
        setDate(moment().startOf("isoWeek").format("YYYY-MM-DD"));
      }
      setIsSearching(false);
    }
  }, [showAs]);

  const loadPatientInfo = async () => {
    try {
      setLoadUser(true);
      const res = await UserServices.getProfile(username);
      if (res.status >= 200 && res.status < 300) {
        setPatient(res.data.my_contact_info);
      }
      setLoadUser(false);
    } catch (error) {
      setLoadUser(false);
      console.log(error);
    }
  };

  const getPatientMedicines = async () => {
    setLoading(true);

    try {
      const res1 = await UserServices.getAssignMedicine(
        username,
        subscription_medicine_patient_id
      );
      if (res1.status === 200 && res1.data.medicines.length > 0) {
        setDataMedicine(res1.data.medicines[0]);
      }

      let res = await DashServices.getMedicineTaken(
        res1.data.medicines[0].medicine_id,
        username,
        dateInterval.from,
        dateInterval.to
      );
      if (res.status >= 200 && res.status < 300) {
        setDataTable(res.data.medicines);
        // filtro medicinas tomadas
        let taken = res.data.medicines
          .filter((i) => i?.status === "tomado" || i?.status === "manual")
          .map((i) => ({
            id: i.id,
            title: i.name,
            start:
              i.date_time.indexOf("00:00:00") === -1
                ? new Date(i.date_time)
                : new Date(i.date_time.replace("00:00:00", "00:00:01")),
            end:
              i.date_time.indexOf("00:00:00") === -1
                ? new Date(i.date_time)
                : new Date(i.date_time.replace("00:00:00", "00:00:01")),
            allDay: false,
            date_time: i.date_time,
            dosage: i.dosage,
          }));

        // label cantidad de dosis del día
        let dosages = {};
        taken.forEach((i) => {
          const date = moment(i.date_time).format("YYYY-MM-DD");
          const tz = i.date_time.substring(19, 25);
          if (!dosages[date]) {
            dosages = {
              ...dosages,
              [date]: {
                dosage: i.dosage,
                date_time: date + "T00:00:00" + tz,
              },
            };
          } else {
            const dosage =
              parseFloat(dosages[date].dosage) + parseFloat(i.dosage);
            dosages[date] = { ...dosages[date], dosage: dosage };
          }
        });

        const dosagesLabels = Object.keys(dosages).map((i, k) => {
          return {
            id: "dsg_" + k,
            title: dosages[i].dosage.toFixed(2),
            start: new Date(dosages[i].date_time),
            end: new Date(dosages[i].date_time),
            allDay: true,
            date_time: dosages[i].date_time,
            dosage: dosages[i].dosage,
          };
        });

        // agregamos los labels
        taken = [...taken, ...dosagesLabels];

        setData(taken);
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const antIcon = (
    <LoadingOutlined
      style={{
        fontSize: 24,
      }}
      spin
    />
  );

  const columns = [
    {
      title: "Fecha",
      dataIndex: "date_time",
      key: "date_time",
      render: (_, record) =>
        moment(record.date_time).format(
          get_language === "en" ? "MM/DD/YYYY HH:mm:ss" : "DD/MM/YYYY HH:mm:ss"
        ),
      sorter: (a, b) => {
        if (a.date_time < b.date_time) return -1;
        if (a.date_time > b.date_time) return 1;
        return 0;
      },
    },
    {
      title: "Dosis",
      dataIndex: "dosage",
      key: "dosage",
      sorter: (a, b) => {
        if (a.dosage < b.dosage) return -1;
        if (a.dosage > b.dosage) return 1;
        return 0;
      },
    },
  ];
  const handlePrev = () => {
    setDate((v) => moment(v).subtract(7, "days").format("YYYY-MM-DD"));
    setDateInterval({
      from: moment(dateInterval.from, "YYYY-MM-DD")
        .subtract(7, "days")
        .format("YYYY-MM-DD"),
      to: moment(dateInterval.to, "YYYY-MM-DD")
        .subtract(7, "days")
        .format("YYYY-MM-DD"),
    });
  };
  const handleToday = () => {
    setIsSearching(false);
    setDate(moment().startOf("isoWeek").format("YYYY-MM-DD"));
    setDateInterval(defaultDateInterval);
  };
  const handleNext = () => {
    if (date !== moment().format("YYYY-MM-DD")) {
      setDate((v) => moment(v).add(7, "days").format("YYYY-MM-DD"));
      setDateInterval({
        from: moment(dateInterval.from, "YYYY-MM-DD")
          .add(7, "days")
          .format("YYYY-MM-DD"),
        to: moment(dateInterval.to, "YYYY-MM-DD")
          .add(7, "days")
          .format("YYYY-MM-DD"),
      });
    }
  };

  const handleHideDetails = () => {
    setModalDetails(false);
  };

  const handleDateIntervalChange = (date, value) => {
    setDateInterval((prevValues) => ({
      ...prevValues,
      [value]: moment(date).format("YYYY-MM-DD"),
    }));
  };

  return (
    <>
      <Row className="w-full">
        <Col span={24} className="mt-4">
          <Title level={2} className="text-center">
            {translate("Toma de ")}{" "}
            {user && patient?.user && `${patient.user.full_name}`}
          </Title>
        </Col>
        <Col span={24}>
          {dataMedicine && (
            <Card>
              <Text className="text-xl">
                {" "}
                {translate("Droga")} {dataMedicine.commercial_brand} (
                {dataMedicine.name})
              </Text>
              <div className="mt-4">
                {[...Array(7)].map((i, k) => {
                  const enabled =
                    dataMedicine.indication.days.indexOf(k.toString()) !== -1;
                  return (
                    <Tag key={k} color={enabled ? "#3BBE50" : "#E8E8E8"}>
                      {weekDays[k]}
                    </Tag>
                  );
                })}
              </div>
              <div className="mt-2">
                {dataMedicine.indication.time.map((tm, k) => (
                  <Badge
                    key={k}
                    className="block"
                    color="geekblue"
                    text={`${tm.substr(0, 5)} hs. (${
                      dataMedicine.indication.dosage[k]
                    } dosis)`}
                  />
                ))}
              </div>
            </Card>
          )}
        </Col>
      </Row>
      <Row className="mt-4">
        <Col span={24} className="flex flex-row justify-end space-x-2">
          <span className="flex flex-col justify-center">
            {translate("Formato")}:
          </span>
          <Button
            icon={<CalendarOutlined />}
            onClick={() => setShowAs("calendar")}
            type={showAs === "calendar" ? "primary" : "default"}
          />
          <Button
            icon={<TableOutlined />}
            onClick={() => setShowAs("table")}
            type={showAs === "table" ? "primary" : "default"}
          />
        </Col>
      </Row>
      {showAs === "calendar" && (
        <Calendar
          localizer={localizer}
          defaultDate={null}
          defaultView={Views.WEEK}
          views={["week"]}
          events={data || []}
          startAccessor="start"
          endAccessor="end"
          step={60}
          timeslots={6}
          style={{ height: 500 }}
          components={{
            toolbar: (props) => (
              <div className="flex flex-row my-4">
                <div>
                  <SecondaryButton
                    className="mr-4"
                    onClick={() => {
                      props.onNavigate("PREV");
                      handlePrev();
                    }}
                  >
                    &lt;
                  </SecondaryButton>
                  <SecondaryButton
                    className="mr-4"
                    onClick={() => {
                      props.onNavigate("TODAY");
                      handleToday();
                    }}
                  >
                    {translate("Hoy")}
                  </SecondaryButton>
                  <SecondaryButton
                    className=""
                    onClick={() => {
                      props.onNavigate("NEXT");
                      handleNext();
                    }}
                  >
                    &gt;
                  </SecondaryButton>
                </div>
                <div className="flex flex-1 flex-row justify-center items-center">
                  {props.label}
                </div>
              </div>
            ),
            week: {
              event: ({ event }) => {
                return (
                  <div className="text-center w-full cursor-default">
                    {event.start.toString().indexOf("00:00:00") === -1
                      ? moment(event.start).format("HH:mm")
                      : event.title}
                  </div>
                );
              },
            },
          }}
          formats={{
            eventTimeRangeFormat: () => {
              return "";
            },
            timeGutterFormat: (date, culture, localizer) => {
              const time = localizer.format(date, "HH:mm", culture);
              let rs = "";
              switch (time) {
                case "00:00":
                  rs = "";
                  break;
                case "06:00":
                  rs = "";
                  break;
                case "12:00":
                  rs = "";
                  break;
                case "18:00":
                  rs = "";
                  break;
              }
              return rs;
            },
          }}
        />
      )}

      {showAs === "table" && (
        <>
          <div className="flex flex-row my-4">
            <div>
              <SecondaryButton
                className="mr-4"
                onClick={() => {
                  handlePrev();
                }}
                disabled={isSearching}
              >
                &lt;
              </SecondaryButton>
              <SecondaryButton
                className="mr-4"
                onClick={() => {
                  handleToday();
                }}
              >
                Hoy
              </SecondaryButton>
              <SecondaryButton
                className=""
                onClick={() => {
                  handleNext();
                }}
                disabled={isSearching}
              >
                &gt;
              </SecondaryButton>
            </div>
          </div>
          <div className="flex flex-col">
            <div className="mb-4 flex items-center">
              <label className="w-14">Desde </label>
              <DatePicker
                onChange={(date) => handleDateIntervalChange(date, "from")}
                format={"YYYY/MM/DD"}
                value={dateInterval.from ? moment(dateInterval.from) : null}
                allowClear={false}
              />
            </div>
            <div className="flex items-center">
              <label className="w-14">Hasta </label>
              <DatePicker
                onChange={(date) => handleDateIntervalChange(date, "to")}
                format={"YYYY/MM/DD"}
                value={dateInterval.to ? moment(dateInterval.to) : null}
                allowClear={false}
              />
            </div>
            <PrimaryButton
              className="w-20 mt-4 text-center"
              disabled={dateInterval.from === "" || dateInterval.to === ""}
              onClick={() => {
                setIsSearching(true);
                setDate(dateInterval.from);
              }}
            >
              {translate("Buscar")}
            </PrimaryButton>
          </div>
          <Table
            rowKey={(i) => i.id}
            className="w-full mt-6"
            dataSource={dataTable}
            columns={columns}
            loading={
              loading
                ? { indicator: antIcon, className: "text-custom_blue" }
                : false
            }
            locale={{
              emptyText: <Empty description={translate("No hay datos")} />,
              sortTitle: translate("Ordenar"),
              triggerDesc: translate("Ordenar (descendente)"),
              triggerAsc: translate("Ordenar (ascendente)"),
              cancelSort: translate("Cancelar orden"),
              filterTitle: translate("Filtros"),
              filterConfirm: translate("Aceptar"),
              filterReset: translate("Limpiar"),
              filterEmptyText: translate("No hay filtros"),
              filterCheckall: translate("Seleccinar todos"),
              filterSearchPlaceholder: translate("Buscar en filtros"),
            }}
          />
        </>
      )}
      <Modal
        open={modalDetails}
        title={translate("Medicamento")}
        onCancel={handleHideDetails}
        footer={[
          <Button key="ok" type="primary" onClick={handleHideDetails}>
            {translate("Aceptar")}
          </Button>,
        ]}
      >
        {translate("Servicio aun no habilitado")}
      </Modal>
    </>
  );
};

export default PatientMedicineTakenTable;
